import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { TableAction, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { FileText, Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBillRuns } from "../../hooks";
import { BulkBillingFilters } from "./BulkBillFilters";

interface BulkBillingFiltersType {
  period_start: string | null;
  period_end: string | null;
  page: number;
}

export const BulkBillingLanding = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<BulkBillingFiltersType>({
    period_start: null,
    period_end: null,
    page: 1,
  });

  const formatFiltersForHook = (
    filters: BulkBillingFiltersType
  ): Record<string, any> => {
    const hookFilters: Record<string, any> = {
      page: currentPage,
      limit: PAGE_SIZE,
      remote_utility_id: getRemoteUtilityId(),
    };

    // Add search term
    if (searchTerm.trim()) {
      hookFilters.search = searchTerm;
    }

    // Add date filters
    if (filters.period_start) {
      hookFilters.period_start = filters.period_start;
    }
    if (filters.period_end) {
      hookFilters.period_end = filters.period_end;
    }

    return hookFilters;
  };

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handleFiltersChange = (newFilters: BulkBillingFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      period_start: null,
      period_end: null,
      page: 1,
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleView = (row: any) => {
    navigate(`/bx/bulk-billing/detail/${row.runId}`);
  };

  const handleEdit = async (bill: any) => {
    console.log("billdata", bill);
    const steps = [
      "bill-cycle",
      "fetch-meter",
      "generate-bill",
      "review-bills",
      "approve-bills",
    ];
    const stepIndex = bill?.step?.stepWorkingOn ?? 0;
    const selectedStep = steps[stepIndex];
    const draftId = bill?.id;
    const cycleId = bill?.cycleId;
    const runId = bill?.runId || "0";

    if (selectedStep) {
      navigate(
        `/bx/bulk-billing/create/${draftId}/${cycleId}/${runId}/edit#${selectedStep}`
      );
    }
  };

  const handleCreateBulkBills = () => {
    navigate("/bx/bulk-billing/create");
  };

  // Define columns for the generic table
  const columns: TableColumn<any>[] = [
    {
      key: "cycleName",
      header: "Cycle Name",
      type: "custom",
      render: (value: any, row: any) => (
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-500" />
          <span>{row.cycleName}</span>
        </div>
      ),
    },
    {
      key: "period",
      header: "Period",
      type: "text",
      render: (value: any, row: any) => (
        <span>
          {row.periodStart} to {row.periodEnd}
        </span>
      ),
    },
    {
      key: "consumers",
      header: "Consumers",
      type: "text",
    },
    {
      key: "totalBills",
      header: "Total Bills",
      type: "text",
    },
    {
      key: "actualBills",
      header: "Actual Bills",
      type: "text",
    },
    {
      key: "estimatedBills",
      header: "Estimated Bills",
      type: "text",
    },
    {
      key: "failedBills",
      header: "Failed Bills",
      type: "text",
    },
    {
      key: "excludedConsumers",
      header: "Excluded Consumers",
      type: "text",
    },
  ];

  // Define actions for the generic table
  const actions: TableAction[] = [
    {
      label: "View",
      onClick: handleView,
      icon: "Eye",
      variant: "outline",
    },
    // {
    //   label: "Edit",
    //   onClick: handleEdit,
    //   icon: "Pencil",
    //   variant: "outline",
    //   hidden: (row: any) => row.status !== "IN-PROGRESS",
    // },
  ];
  const { data: billList } = useBillRuns(formatFiltersForHook(filters));
  // Calculate pagination info
  const totalItems = billList?.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <PageHeader
            title={"Bulk Billing"}
            subtitle="Manage and generate bills for multiple consumers in bulk."
          />
          <Button
            onClick={handleCreateBulkBills}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Bulk Bills
          </Button>
        </div>

        {/* Table */}
        <div>
          <GenericTable
            data={billList?.result || []}
            columns={columns}
            rowKey="id"
            searchPlaceholder="Search bulk bills..."
            searchParamName="query"
            searchDebounceMs={300}
            onSearch={handleSearch}
            actions={actions}
            actionsType="icons"
            emptyMessage="No bulk bills found"
            filterPanel={
              <BulkBillingFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApplyFilters={handleFiltersChange}
                onClearFilters={clearFilters}
              />
            }
            pagination={{
              currentPage,
              totalPages,
              totalItems,
              itemsPerPage: PAGE_SIZE,
              startItem,
              endItem,
            }}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};
